import { api } from '../lib/api.js';
import { useAction, useResource } from '../lib/useApi.js';
import { useAuth } from '../lib/auth.js';
import type { AppUser, DirectoryStatus, Role } from '../lib/types.js';
import {
  Banner,
  Card,
  Empty,
  ErrorState,
  Loading,
  Pill,
  Stat,
  When,
  useToast,
} from '../components/ui.js';

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  ADMIN: 'Everything, including role management.',
  MARKETING: 'Design, preview and submit templates. Cannot push to Exchange.',
  APPROVER: 'Approve or reject a version, and publish it. Cannot author.',
  IT: 'Deploy, schedule and roll back. Read-only on template design.',
  VIEWER: 'Read-only everywhere, including the audit trail.',
  STAFF: 'Their own signature and nothing else. The default for a new sign-in.',
};

/** Administration: directory sync, role assignment, and the configuration picture. */
export function AdminPage(): JSX.Element {
  const { me, config, refreshMe } = useAuth();
  const toast = useToast();

  const users = useResource<{ users: AppUser[] }>('/api/users');
  const directory = useResource<DirectoryStatus>('/api/directory/status');
  const graphConfig = useResource<{
    graphConfigured: boolean;
    tenantIdConfigured: boolean;
    credentialType: string;
    keyVaultConfigured: boolean;
  }>('/api/directory/config');

  const canManage = Boolean(me?.capabilities.canManageUsers);
  const canSync = Boolean(me?.capabilities.canSyncDirectory);

  const sync = useAction(async () => {
    const result = await api.post<{
      userCount: number;
      groupCount: number;
      removedCount: number;
      durationMs: number;
    }>('/api/directory/sync');
    toast.success(
      'Directory synced',
      `${result.userCount} users, ${result.groupCount} groups, ${result.removedCount} removed in ${result.durationMs}ms.`,
    );
    await directory.reload();
  });

  const setRole = useAction(async (user: AppUser, role: Role) => {
    await api.patch(`/api/users/${user.id}`, { role });
    toast.success('Role updated', `${user.displayName} is now ${role.toLowerCase()}.`);
    await users.reload();
    if (user.id === me?.id) await refreshMe();
  });

  const setActive = useAction(async (user: AppUser, isActive: boolean) => {
    await api.patch(`/api/users/${user.id}`, { isActive });
    toast.success(isActive ? 'Account reactivated' : 'Account deactivated');
    await users.reload();
  });

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Administration</h1>
          <p className="sub">Directory sync, who can do what, and how this instance is configured.</p>
        </div>
        <div className="page-actions">
          {canSync && (
            <button className="primary" disabled={sync.pending} onClick={() => void sync.run()}>
              {sync.pending ? 'Syncing…' : 'Sync directory now'}
            </button>
          )}
        </div>
      </header>

      <div className="page-body">
        {sync.error && <ErrorState error={sync.error} />}
        {setRole.error && <ErrorState error={setRole.error} />}
        {setActive.error && <ErrorState error={setActive.error} />}

        {config.auth.devAuthBypass && (
          <Banner tone="danger">
            <strong>Development authentication bypass is on</strong>
            <div className="tiny">
              Every request is trusted and the identity comes from a header. The API refuses to start
              with this enabled when <code>NODE_ENV=production</code>, but it must not be left on
              anywhere shared.
            </div>
          </Banner>
        )}

        {graphConfig.data && !graphConfig.data.graphConfigured && (
          <Banner tone="warn">
            <strong>Microsoft Graph is not configured</strong>
            <div className="tiny">
              Directory sync and live deployments need the daemon app registration. Set{' '}
              <code>GRAPH_TENANT_ID</code>, <code>GRAPH_CLIENT_ID</code> and a credential. See{' '}
              <code>docs/GRAPH-SETUP.md</code>.
            </div>
          </Banner>
        )}

        <div className="grid cols-4 mb">
          <Stat
            label="Directory users"
            value={directory.data?.userCount ?? '—'}
            note={
              directory.data?.lastSuccessfulSyncAt ? (
                <>
                  synced <When iso={directory.data.lastSuccessfulSyncAt} />
                </>
              ) : (
                'never synced'
              )
            }
            tone={directory.data?.isStale ? 'warn' : 'ok'}
          />
          <Stat label="Groups" value={directory.data?.groupCount ?? '—'} />
          <Stat label="App users" value={users.data?.users.length ?? '—'} />
          <Stat
            label="Credential"
            value={graphConfig.data?.credentialType ?? '—'}
            note={
              graphConfig.data?.keyVaultConfigured
                ? 'resolved from Key Vault'
                : 'from environment settings'
            }
            tone={graphConfig.data?.credentialType === 'certificate' ? 'ok' : undefined}
          />
        </div>

        {directory.data?.lastRun?.error && (
          <Banner tone="danger">
            <strong>The last directory sync failed</strong>
            <div className="tiny">{directory.data.lastRun.error}</div>
          </Banner>
        )}

        <div className="grid cols-2">
          <Card title="People and roles" tight>
            {users.error && <ErrorState error={users.error} />}
            {users.loading && !users.data && (
              <div style={{ padding: 14 }}>
                <Loading rows={3} />
              </div>
            )}
            {users.data && users.data.users.length === 0 && (
              <Empty title="No users yet">
                A row appears here the first time somebody signs in.
              </Empty>
            )}
            {users.data && users.data.users.length > 0 && (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Role</th>
                      <th>Last sign-in</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {users.data.users.map((u) => (
                      <tr key={u.id} style={{ opacity: u.isActive ? 1 : 0.5 }}>
                        <td>
                          <div className="cell-strong">
                            {u.displayName}
                            {u.id === me?.id && <Pill tone="accent"> you</Pill>}
                            {!u.isActive && <Pill tone="outline"> deactivated</Pill>}
                          </div>
                          <div className="tiny mono muted">{u.upn}</div>
                        </td>
                        <td>
                          {canManage ? (
                            <select
                              value={u.role}
                              onChange={(e) => void setRole.run(u, e.target.value as Role)}
                              disabled={setRole.pending}
                              aria-label={`Role for ${u.displayName}`}
                            >
                              {(Object.keys(ROLE_DESCRIPTIONS) as Role[]).map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <Pill tone="outline">{u.role.toLowerCase()}</Pill>
                          )}
                        </td>
                        <td className="tiny muted nowrap">
                          <When iso={u.lastLoginAt} empty="never" />
                        </td>
                        <td className="right">
                          {canManage && u.id !== me?.id && (
                            <button
                              className={u.isActive ? 'sm danger' : 'sm'}
                              disabled={setActive.pending}
                              onClick={() => void setActive.run(u, !u.isActive)}
                            >
                              {u.isActive ? 'Deactivate' : 'Reactivate'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="col">
            <Card title="What each role can do">
              <table className="data">
                <tbody>
                  {(Object.keys(ROLE_DESCRIPTIONS) as Role[]).map((role) => (
                    <tr key={role}>
                      <td className="cell-strong nowrap">{role}</td>
                      <td className="tiny">{ROLE_DESCRIPTIONS[role]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="tiny muted mt">
                Roles are managed here rather than through Entra app roles, so changing one takes
                effect on that person&apos;s next request without an Entra admin.
              </p>
            </Card>

            <Card title="Configuration">
              <table className="data">
                <tbody>
                  <Row label="Sign-in tenant" value={config.auth.tenantId ?? 'not configured'} />
                  <Row label="Web client id" value={config.auth.clientId ?? 'not configured'} />
                  <Row
                    label="Graph (daemon)"
                    value={graphConfig.data?.graphConfigured ? 'configured' : 'not configured'}
                  />
                  <Row label="Graph credential" value={graphConfig.data?.credentialType ?? '—'} />
                  <Row
                    label="Key Vault"
                    value={graphConfig.data?.keyVaultConfigured ? 'in use' : 'not in use'}
                  />
                  <Row
                    label="Global dry run"
                    value={config.features.globalDryRun ? 'ON — nothing is applied' : 'off'}
                  />
                </tbody>
              </table>
              <p className="tiny muted mt">
                Secrets are never returned by the API; only whether one is present.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <tr>
      <td className="tiny muted nowrap">{label}</td>
      <td className="tiny mono">{value}</td>
    </tr>
  );
}
